import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData, graphql } from '@packages/gql/generated/shopping';
import type { DownloadType } from '@packages/gql/generated/shopping/graphql';
import { Button, Link } from '@packages/shared';
import { Pdf32 } from '@packages/themes/icons';
import type { FC } from 'react';
import { defineMessages, useIntl } from 'react-intl';

const DownloadFragment = graphql(/* GraphQL */ `
  fragment DownloadFragment on Download {
    link
    type
  }
`);

export type DownloadProps = {
  /**
   * Download data from current product.
   */
  downloadData: FragmentType<typeof DownloadFragment>;
  /**
   * Decides wether or not the download icon should be rendered
   * @default true
   */
  renderIcon?: boolean;
  /**
   * Optional - displays the index as text next to the message.
   */
  idx?: number;
};

const messages = defineMessages<DownloadType>({
  ASSEMBLY_INSTRUCTIONS: {
    defaultMessage: 'Aufbauanleitung',
    id: 'efficiencyFlagDownloads.type.assembly',
  },
  PRODUCT_DATASHEET: {
    defaultMessage: 'Produktdatenblatt',
    id: 'efficiencyFlagDownloads.type.productdatasheet',
  },
  SAFETY_DATASHEET: {
    defaultMessage: 'Sicherheitsdatenblatt',
    id: 'efficiencyFlagDownloads.type.safetydatasheet',
  },
  USER_MANUAL: {
    defaultMessage: 'Gebrauchsanleitung',
    id: 'efficiencyFlagDownloads.type.usermanual',
  },
  MANUFACTURER_WARRANTY: {
    defaultMessage: 'Hersteller Garantie',
    id: 'efficiencyFlagDownloads.type.manufacturerwarranty',
  },
  WARNING_LABEL: {
    defaultMessage: 'Warnhinweis',
    id: 'efficiencyFlagDownloads.type.warninglabel',
  },
});

/**
 * The Download component is used to display a link with or without an icon.
 * The link is clickable and opens a downloadable file in a new tab.
 * */
export const Download: FC<DownloadProps> = ({
  downloadData: donwloadData,
  renderIcon = true,
  idx,
}) => {
  const { formatMessage } = useIntl();
  const { link, type } = getFragmentData(DownloadFragment, donwloadData);

  // if there is a new DownloadType, which is missing in messages, we return null
  if (!(type in messages)) {
    return null;
  }

  return (
    <Link href={link} noLinkStyle openInNewTab display="inline-flex">
      <Button
        variant="text"
        startIcon={renderIcon && <Pdf32 />}
        sx={[
          {
            p: 0,
            minHeight: 'inherit',
            justifyContent: 'start',
            ':hover': { background: 'none' },
          },
          !renderIcon && {
            typography: 'body3',
            textDecoration: 'underline',
            ':hover': { textDecoration: 'underline' },
          },
        ]}
      >
        {formatMessage(messages[type])} {idx}
      </Button>
    </Link>
  );
};
